import * as React from 'react';
import { EQuickLookType, ICardProps } from './types';
import { CARD_CLASS, CARD_CONTROL_CLASS } from '../../../../constants';
import { CardIndicator } from '../../../CardIndicator/CardIndicator';
import { CommentCountIndicator } from '../../../CommentCoundIndicator/CommentCountIndicator';
import { AttachmentsCountIndicator } from '../../../AttachmentsCountIndicator/AttachmentsCountIndicator';
import { SubCardsCountIndicator } from '../../../subcardsCountIndicator/SubCardsCountIndicator';
import { ChecklistsCountIndicator } from '../../../ChecklistsCountIndicator/ChecklistsCountIndicator';
import { CardCollaborators } from '../../../CardCollaborators/CardCollaborators';
import './Card.scss';
import { CardNumber } from '../../../CardNumber/CardNumber';
import { CardActionsHOC } from '../../../CardActions/hocs/CardActionsHOC';
import { CardTags } from '../../../CardTags/components/CardTags/CardTags';
import { DescriptionIndicator } from '../../../DescriptionIndicator/DescriptionIndicator';
import { RecurringHeaderHOC } from '../../../RecurringHeader/hocs/RecurringHeaderHOC/RecurringHeaderHOC';
import { CardChecklistsHOC } from '../../../CardChecklists/hocs/CardChecklistsHOC/CardChecklistsHOC';
import { CardSubcardsHOC } from '../../../CardSubcards/hocs/CardSubcardsHOC/CardSubcardsHOC';
import { CardAgingIndicator } from '../../../CardAgingIndicator/CardAgingIndicator';
import { ECardAgingIndicatorType } from '../../../../../../../../types/rest/ICardAging';
import { Checkbox, Loader } from 'kui';
import { CardDescription } from '../../../CardDescription/CardDescription';
import { CardDefaultTemplate } from '../../../CardDefaultTemplate/CardDefaultTemplate';
import { CardProgressHOC } from '../../../../../../base/components/CardProgress/hocs/CardProgressHOC';
import { QuickLookHOC } from '../../../QuickLook/hocs/QuickLookHOC/QuickLookHOC';
import { DriveDocs } from '../../../driveDocs/components/DriveDocs/DriveDocs';
import { DriveDocImgsHOC } from '../../../driveDocs/hocs/DriveDocImgsHOC/DriveDocImgsHOC';
import { CardIndicatorsMain } from '../../../CardIndicatorsMain/components/CardIndicatorsMain';
import { GOOGLE_SPACING } from 'app/const';
import { CardColorTagsHOC } from '../../../CardColorTags/hocs/CardColorTagsHOC';
import { SubCardIndicatorHOC } from '../../../subCardIndicator/hocs/SubCardIndicatorHOC';
import { NO_PRIORITY } from '../../../../../../helpers/constants';
import { SegmentCardType } from '../../../../../../../../middlewares/segment/trackEntities/cardEvents';
import { CARD_RENDER_MAX_TIME } from './constants';
import { CardCustomProperties } from '../../../CardCustomProperties/components/CardCustomProperties';
import { CardTitleHOC } from '../../../CardTitle/hocs/CardTitleHOC/CardTitleHOC';
import { getParentsClasses } from '../../../../../../helpers/getParentsClasses';
import { ESegmentClickType } from '../../../../../../../../middlewares/segment/constants';
import { AnalyzeRender } from 'app/view/react_components/helpers/memoizeHelper';
import { root } from 'app/store/constants';

interface IRenderProps extends ICardProps {
    cardRef?: any;
    editingName: string;
    className?: string;
    isClone?: boolean;
    isColorTagsOpened: boolean;
    isLoadingQuickLook?: boolean;
    isOpenColorTagsDropdown: boolean;
    isTitleEditing: boolean;
    modulesRef?: any;
    style?: React.CSSProperties;

    setEditingName: (name: string) => void;
    setTitleEditing: (isEditing: boolean) => void;
    onClick?: (e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onQuickLook?: (e: EQuickLookType) => void;
    setCardHeightAndProps?: () => void;
    setColorTagsOpened: (opened: boolean) => void;
    setOpenColorTagsDropdown: (opened: boolean) => void;
}

const analyzeRender = root.Settings.development ? new AnalyzeRender(`CardRender`) : null;

export function CardRender({
    cardRef = null,
    className = '',
    editingName,
    isClone,
    isColorTagsOpened,
    isLoadingQuickLook,
    isOpenColorTagsDropdown,
    modulesRef = null,
    isTitleEditing,
    style = null,

    onClick,
    onKeyDown,
    onQuickLook,
    setCardHeightAndProps,
    setColorTagsOpened,
    setEditingName,
    setOpenColorTagsDropdown,
    setTitleEditing,

    actionsPortalId,
    assignees,
    attachmentsCount,
    backgroundColor,
    boardId,
    boardRoles,
    cardAgingIndicator,
    cardNumber,
    isShowChecklistsCounter,
    checklists,
    checklistsItemsCount,
    collaborators,
    colorTags,
    commentCount,
    customProperties,
    description,
    doneChecklistsCount,
    dragHandleProps,
    dueDate,
    epicName,
    estimate,
    hasDescription,
    height,
    hideActions,
    id,
    isDefaultTemplate,
    isMultiselect,
    isPinnedCustomProperties,
    isPinnedDescription,
    isPinnedSubcards,
    isReadonly,
    isRecurring,
    isRelatedCardEdit,
    isRelatedPanel,
    isSelected,
    isShowAgeing,
    isShowAssignees,
    isShowColorTags,
    isShowCounters,
    isShowDueDate,
    isShowEmptyProperties,
    isShowEstimate,
    isShowPrefixes,
    isShowPriority,
    isShowProgress,
    isShowStartDate,
    isShowTags,
    isQuickLookCollapse,
    isTemplate,
    listId,
    mainDriveDocSet,
    name,
    openedQuickLook,
    percent,
    pinnedChecklistCount,
    priority,
    recurringTasks,
    startDate,
    subcards,
    subCardsCount,
    tags,
    timeSinceLastActivity,
    timeSinceMovedToList,
    viewListId,
    onCheckboxClick,
    onDoubleClickEvent
}: IRenderProps){

    if (analyzeRender) {
        analyzeRender.call(`${id} isClone=${isClone}`, arguments[0]);
    }
    const cardHasPercent = percent > 0;
    const cardHasTags = tags && tags.length > 0;
    const isShowCardAgingIndicator = cardAgingIndicator && (
        cardAgingIndicator === ECardAgingIndicatorType.BY_LIST && !!timeSinceMovedToList ||
        cardAgingIndicator === ECardAgingIndicatorType.BY_ACTIVITY && !!timeSinceLastActivity
    );
    const isCardHasIndicators = (
        cardNumber ||
        isDefaultTemplate ||
        cardHasPercent ||
        !!epicName ||
        isShowCardAgingIndicator
    );

    const isCardHasCountersOrDescription = (
        subCardsCount > 0 && !isPinnedSubcards ||
        commentCount > 0 ||
        epicName ||
        attachmentsCount > 0 ||
        isShowChecklistsCounter ||
        hasDescription && !isPinnedDescription
    );

    const cardModuleClass = CARD_CLASS + '__module';
    const cardCheckboxClass = CARD_CLASS + '__checkbox';
    const cardIndicatorsClass = cardModuleClass + '--indicators';

    const [isDatesOpened, setDatesOpened] = React.useState(null);
    const [clickType, setClickType] = React.useState(null);

    // KNB-4626 на клоне ждем пока все картинки загрузятся, чтобы пересчитать высоту
    let [loadingImgs, _setLoadingImgs] = React.useState([]);
    const setLoadingImgs = (value: string[]) => {
        loadingImgs = value;
        _setLoadingImgs(value);
    };
    const onToggleLoadingImg = (id: string) => {
        if (!isClone) return;
        if (!loadingImgs.includes(id)) {
            setLoadingImgs([...loadingImgs, id]);
        } else {
            setLoadingImgs(loadingImgs.filter(item => item !== id));
        }
    };

    const cardNameTimerRef = React.useRef(null);

    const onDoubleClick = () => {
        if (document.documentElement.classList.contains('touch')) return;
        onDoubleClickEvent();
        setClickType(ESegmentClickType.double)
        if (cardNameTimerRef.current) clearTimeout(cardNameTimerRef.current);
        setTitleEditing(true);
    };

    const handleClick = (
        e: React.MouseEvent
    ) => {
        if (isTitleEditing) return;

        if (cardNameTimerRef.current) clearTimeout(cardNameTimerRef.current);

        const parentClasses = getParentsClasses(e.target as HTMLElement, [
            CARD_CONTROL_CLASS
        ]);
        if (parentClasses.includes(CARD_CONTROL_CLASS) && !isMultiselect) return;

        cardNameTimerRef.current = setTimeout(() => {
            onClick(e);
        }, 500);
    };

    const [cardStyle, setCardStyle] = React.useState(style);
    React.useEffect(() => {
        let h: number | string = 'auto';
        if (!isClone && height) {
            let margin = GOOGLE_SPACING * 1.25;
            if (collaborators.length) margin -= GOOGLE_SPACING * 0.25;
            h = height - margin;
        }
        setCardStyle({
            ...style,
            height: h,
        })
    }, [ height, style ]);

    const [ mainDriveDocs, setMainDriveDocs ] = React.useState([]);
    const [ mainDriveDocsImgs, setMainDriveDocsImgs ] = React.useState([]);
    React.useEffect(() => {
        setMainDriveDocs(mainDriveDocSet.filter(driveDoc => !driveDoc.isImage));
        setMainDriveDocsImgs(mainDriveDocSet.filter(driveDoc => driveDoc.isImage));
    }, [mainDriveDocSet]);

    const [isActive, setActive] = React.useState(true);
    React.useEffect(() => {
        const onVisibilityChange = () => {
            setActive(!document.hidden);
        }
        if (isClone) {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }
        return () => {
            if (isClone) {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
        }
    }, []);

    const [isNeedCalc, setNeedCalc] = React.useState(true);
    const timerRef = React.useRef(null);

    const onHeightChange = () => setNeedCalc(true);

    React.useEffect(() => {
        //console.log('setNeedCalc(true);')
        onHeightChange();
    }, [
        isActive,
        actionsPortalId,
        assignees,
        attachmentsCount,
        backgroundColor,
        boardId,
        boardRoles,
        cardAgingIndicator,
        cardNumber,
        isShowChecklistsCounter,
        isQuickLookCollapse,
        checklistsItemsCount,
        collaborators,
        colorTags,
        commentCount,
        description,
        doneChecklistsCount,
        dragHandleProps,
        editingName,
        epicName,
        estimate,
        hasDescription,
        height,
        hideActions,
        id,
        isDefaultTemplate,
        isMultiselect,
        customProperties,
        subcards,
        checklists,
        isOpenColorTagsDropdown,
        isPinnedCustomProperties,
        isPinnedDescription,
        isPinnedSubcards,
        isReadonly,
        isRecurring,
        isRelatedCardEdit,
        isRelatedPanel,
        isSelected,
        isShowAgeing,
        isShowAssignees,
        isShowColorTags,
        isShowCounters,
        isShowDueDate,
        isShowEmptyProperties,
        isShowEstimate,
        isShowPrefixes,
        isShowPriority,
        isShowProgress,
        isShowStartDate,
        isShowTags,
        isTemplate,
        listId,
        mainDriveDocSet,
        name,
        openedQuickLook,
        percent,
        pinnedChecklistCount,
        recurringTasks,
        subcards,
        subCardsCount,
        tags,
        timeSinceLastActivity,
        timeSinceMovedToList,
        viewListId,
    ]);

    React.useLayoutEffect(() => {
        if (isClone && isActive) {
            //console.log('useLayoutEffect setCardHeightAndProps')
            setCardHeightAndProps();
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                //console.log('setNeedCalc(false);')
                setNeedCalc(false);
            }, CARD_RENDER_MAX_TIME);

        }
    });

    // React.useEffect(() => {
    //     if (isClone && cardRef.current && isNeedCalc) {
    //         new ResizeObserver(() => {
    //             setCardHeight();
    //         }).observe(cardRef.current);
    //     }
    // }, [isNeedCalc]);

    if (isClone && !isNeedCalc && !loadingImgs.length) return null;

    const isShowColorTagsElement = isShowColorTags && (colorTags.length > 0 || isColorTagsOpened) || isOpenColorTagsDropdown;

    if (isShowColorTagsElement) {
        className += ` ${CARD_CLASS}--w-color-tags`;
    }

    if (isTitleEditing) {
        className += ` ${CARD_CLASS}--rename`;
    }

    return (
        <div
            className={className}
            ref={cardRef}
            style={cardStyle}
        >
            <div
                className={CARD_CLASS + '__modules'}
                ref={modulesRef}
                onClick={handleClick}
                onKeyDown={onKeyDown}
            >
                <div
                    className={CARD_CLASS + '__draghandle'}
                    {...dragHandleProps}
                >
                    {!!mainDriveDocsImgs.length &&
                        <DriveDocImgsHOC
                            cardId={id}
                            images={mainDriveDocsImgs}
                        />
                    }
                    {!isReadonly && !hideActions && !isMultiselect && !isRelatedPanel &&
                        (isClone ? <div className={cardModuleClass + '-fake-actions'} />
                            : <CardActionsHOC
                                boardId={boardId}
                                cardId={id}
                                portalId={actionsPortalId}
                                key={listId + '-' + id}
                                portal
                                openColorTags={() => setOpenColorTagsDropdown(true)}
                            />)
                    }
                    {!isReadonly && !hideActions && isMultiselect && !isRelatedPanel &&
                        <Checkbox
                            className={`${cardCheckboxClass} ${CARD_CONTROL_CLASS}`}
                            onChange={onCheckboxClick}
                            checked={isSelected}
                        >
                        </Checkbox>
                    }
                    {isRelatedPanel && !isRelatedCardEdit &&
                        <Checkbox
                            className={`${cardCheckboxClass} ${CARD_CONTROL_CLASS}`}
                            onChange={onCheckboxClick}
                            checked={isSelected}
                        >
                        </Checkbox>
                    }
                    {isRecurring &&
                        <RecurringHeaderHOC recurringTasks={recurringTasks}/>
                    }
                    {isShowColorTagsElement &&
                        <div className={`${cardModuleClass} ${cardModuleClass + '--color-tags'}`}>
                            {!isClone &&
                                <CardColorTagsHOC
                                    cardId={id}
                                    colorTags={colorTags}
                                    setOpened={open => {
                                        setColorTagsOpened(open);
                                        setOpenColorTagsDropdown(open);
                                    }}
                                    isOpened={isOpenColorTagsDropdown}
                                />
                            }
                        </div>
                    }
                    <div className={`${cardModuleClass} ${cardModuleClass + '--title'}`} onDoubleClick={onDoubleClick}>
                        {isCardHasIndicators && !isTitleEditing &&
                            <div
                                className={`
                                    ${cardModuleClass}
                                    ${cardIndicatorsClass}
                                    ${isShowProgress && cardHasPercent ? cardIndicatorsClass + '-progress' : ''}
                                    ${isShowProgress && cardHasPercent && !isShowCardAgingIndicator && !cardNumber && !isDefaultTemplate ? cardIndicatorsClass + '-progress-only' : ''}
                                `}
                            >
                                {isShowProgress && cardHasPercent &&
                                    <CardIndicator type={'processing'}>
                                        <CardProgressHOC
                                            cardId={id}
                                            backgroundColor={backgroundColor}
                                            isLightBgStroke
                                        />
                                    </CardIndicator>
                                }
                                {isShowAgeing && isShowCardAgingIndicator && !isRecurring &&
                                    <CardIndicator>
                                        <CardAgingIndicator
                                            date={
                                                Date.now() - (cardAgingIndicator === ECardAgingIndicatorType.BY_ACTIVITY
                                                        ? timeSinceLastActivity
                                                        : timeSinceMovedToList
                                                )
                                            }
                                            type={cardAgingIndicator}
                                        />
                                    </CardIndicator>
                                }
                                {isShowPrefixes && cardNumber &&
                                    <CardNumber number={cardNumber}/>
                                }
                                {isDefaultTemplate &&
                                    <CardDefaultTemplate/>
                                }
                            </div>
                        }
                        <div className={CARD_CLASS + '__title-inner'}>
                            <CardTitleHOC
                                cardId={id}
                                editingName={editingName}
                                text={name}
                                isClone={isClone}
                                isEditing={isTitleEditing}
                                setEditing={setTitleEditing}
                                setEditingName={setEditingName}
                                clickType={clickType}
                                onHeightChange={isClone && onHeightChange}
                                onToggleLoadingImg={onToggleLoadingImg}
                            />
                        </div>
                    </div>
                    {isPinnedDescription && description &&
                        <CardDescription
                            description={description}
                            onHeightChange={isClone && onHeightChange}
                            onToggleLoadingImg={onToggleLoadingImg}
                        />
                    }
                    {isShowTags && cardHasTags &&
                        <div className={`${cardModuleClass} ${cardModuleClass + '--tags'}`}>
                            <CardTags tags={tags}/>
                        </div>
                    }
                    {isShowCounters && isCardHasCountersOrDescription && (
                        isClone ? <div className={`${cardModuleClass} ${cardModuleClass}-fake-counters`}/>
                            : <div className={`${cardModuleClass} ${cardModuleClass + '--counters'}`}>
                                {!isPinnedDescription && hasDescription && !isClone &&
                                    <DescriptionIndicator
                                        active={EQuickLookType.DESCRIPTION === openedQuickLook}
                                        onClick={() => onQuickLook(EQuickLookType.DESCRIPTION)}
                                    />
                                }
                                {!isPinnedSubcards && subCardsCount > 0 && !isClone &&
                                    <SubCardsCountIndicator
                                        active={EQuickLookType.SUBCARDS === openedQuickLook}
                                        subCardsCount={subCardsCount}
                                        onClick={() => onQuickLook(EQuickLookType.SUBCARDS)}
                                    />
                                }
                                {epicName && !isClone &&
                                    <SubCardIndicatorHOC cardId={id}/>
                                }
                                {commentCount > 0 && !isClone &&
                                    <CommentCountIndicator
                                        active={EQuickLookType.COMMENTS === openedQuickLook}
                                        commentsCount={commentCount}
                                        onClick={() => onQuickLook(EQuickLookType.COMMENTS)}
                                    />
                                }
                                {attachmentsCount > 0 && !isClone &&
                                    <AttachmentsCountIndicator
                                        active={EQuickLookType.ATTACHMENTS === openedQuickLook}
                                        attachmentsCount={attachmentsCount}
                                        onClick={() => onQuickLook(EQuickLookType.ATTACHMENTS)}
                                    />
                                }
                                {isShowChecklistsCounter && !isClone &&
                                    <ChecklistsCountIndicator
                                        active={EQuickLookType.CHECKLISTS === openedQuickLook}
                                        doneChecklistsCount={doneChecklistsCount}
                                        checklistsCount={checklistsItemsCount}
                                        onClick={() => onQuickLook(EQuickLookType.CHECKLISTS)}
                                    />
                                }
                            </div>)
                    }
                    {!isReadonly &&
                        (
                            (isShowEmptyProperties && (isShowAssignees || isShowDueDate || isShowStartDate || isShowPriority)) ||
                            isShowEstimate && estimate ||
                            isShowAssignees && assignees && !!assignees.length ||
                            isShowDueDate && dueDate ||
                            isShowStartDate && startDate ||
                            isShowPriority && priority && priority !== NO_PRIORITY
                        ) &&
                        (isClone ? <div className={cardModuleClass + '-fake-main-indicators'}/>
                            : <CardIndicatorsMain
                                boardId={boardId}
                                listId={viewListId ? viewListId : String(listId)}
                                cardId={id}
                                estimate={estimate}
                                isDatesOpened={isDatesOpened}
                                isShowAssignees={isShowAssignees}
                                isShowDueDate={isShowDueDate}
                                isShowEmptyProperties={isShowEmptyProperties}
                                isShowEstimate={isShowEstimate}
                                isShowPriority={isShowPriority}
                                isShowStartDate={isShowStartDate}
                                assignees={assignees.map(item => item.sharedUser.permissionId)}
                                startDate={startDate}
                                dueDate={dueDate}
                                priority={priority}
                                source={SegmentCardType.CARD_PREVIEW}
                                setDatesOpened={setDatesOpened}
                            />)
                    }
                </div>
                {(
                    openedQuickLook && isShowCounters ||
                    !!mainDriveDocs.length ||
                    !!pinnedChecklistCount ||
                    isPinnedSubcards ||
                    isPinnedCustomProperties
                ) && <>
                    <div
                        className={CARD_CLASS + '__notdraggable'}
                    >
                        {openedQuickLook && isShowCounters &&
                            <div className={`
                                ${cardModuleClass}
                                ${cardModuleClass + '--quick-look'}
                                ${isLoadingQuickLook !== false ? 'loading' : ''}
                            `}>
                                {
                                    !isClone && isLoadingQuickLook === true
                                    && <Loader />
                                }
                                {(isClone || isLoadingQuickLook === false) &&
                                    <QuickLookHOC
                                        cardId={id}
                                        isClone={isClone}
                                        checklists={checklists}
                                        subcards={subcards}
                                        openedQuickLook={openedQuickLook}
                                        onHeightChange={isClone && onHeightChange}
                                        onToggleLoadingImg={onToggleLoadingImg}
                                    />
                                }
                            </div>
                        }
                        {!!mainDriveDocs.length &&
                            <DriveDocs mainDriveDocSet={mainDriveDocs} cardId={id} isClone={isClone} />
                        }
                        {!!pinnedChecklistCount &&
                            <CardChecklistsHOC
                                cardId={id}
                                checklists={checklists}
                                className={`
                                    ${cardModuleClass}
                                    ${cardModuleClass + '--checklists'}
                                `}
                                isReadonly={isReadonly || isTemplate}
                                setCardHeight={setCardHeightAndProps}
                                onHeightChange={isClone && onHeightChange}
                                onToggleLoadingImg={onToggleLoadingImg}
                            />
                        }
                        {isPinnedSubcards &&
                            <CardSubcardsHOC
                                cardId={id}
                                className={`
                                    ${cardModuleClass}
                                    ${cardModuleClass + '--subcards'}
                                `}
                                setCardHeight={setCardHeightAndProps}
                                isClone={isClone}
                                subcards={subcards}
                                onHeightChange={isClone && onHeightChange}
                                onToggleLoadingImg={onToggleLoadingImg}
                            />
                        }
                        {isPinnedCustomProperties &&
                            <CardCustomProperties
                                cardId={id}
                                setCardHeight={setCardHeightAndProps}
                                elements={customProperties}
                                isReadonly={isReadonly}
                                onHeightChange={isClone && onHeightChange}
                                onToggleLoadingImg={onToggleLoadingImg}
                            />
                        }
                    </div>
                </>
                }
            </div>
            {!!collaborators.length && !isClone &&
                <div className={CARD_CLASS + '__state'}>
                    <CardCollaborators
                        collaborators={collaborators}
                        assignees={assignees}
                        roles={boardRoles}
                    />
                </div>
            }
        </div>
    );
};
